import React from 'react';
import ReactDOM from 'react-dom';
import ArenaCombate, { AvatarCuenta } from './arenaCombate';
import corazon from './images/corazon64.png';
import infoImage from './images/info.png';
import { useTutorial } from './components/Tutorial';
import './arena.css';

// Arena Forallia en Mi cuenta (botones Luchar e Historial de user.jsx). Todo lo decide el servidor
// (apps/arena); aquí solo se elige contrincante y se reproduce lo que devuelve.
const api = async (path, body) => {
    try {
        const res = await fetch('/api/arena/' + path, body
            ? { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
            : { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        return { ...data, ok: res.ok, status: res.status };
    } catch (err) {
        return { ok: false, status: 0 };
    }
};

const FACCION = { heroe: 'Héroe', villano: 'Villano', neutro: 'Neutro' };
// Las de la cuenta, con sus nombres del perfil: la fuerza del motor es el Ataque de la cuenta.
const STATS = [['vida', 'Vida'], ['fuerza', 'Ataque'], ['agilidad', 'Agilidad'], ['velocidad', 'Velocidad']];
const LIMITE = 3; // vidas que se recuperan solas; con "Recargar vidas" se puede tener más

// Un corazón por vida, en gris las gastadas de las que se recuperan; muchas de más, en número.
const corazones = (n) => n > 9
    ? <><img src={corazon} alt='' /> ×{n}</>
    : Array.from({ length: Math.max(LIMITE, n) }, (_, i) => <img key={i} src={corazon} alt='' className={i < n ? '' : 'gastado'} />);

const mmss = (ms) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
};
const fecha = (d) => new Date(d).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// Mientras Narux esta en directo no se lucha (el servidor contesta 409).
export const AVISO_DIRECTO = 'Mientras Narux está en directo, los corruptos están viéndole y no pueden luchar. ¡Aprovecha para acumular combates viendo el directo!';

const mensaje = (r) => r.status === 429 ? 'No te quedan combates.'
    : r.status === 409 ? AVISO_DIRECTO
    : r.status === 400 ? 'Ese contrincante ya no está disponible.'
        : r.status === 404 ? 'Ese combate ya no existe.'
            : r.status === 401 ? 'Tu sesión ha caducado. Vuelve a iniciar sesión.'
                : 'Algo ha fallado. Prueba otra vez.';

function Rival({ r, disabled, onClick }) {
    return (
        <button type='button' className='arenaRival' disabled={disabled} onClick={onClick}>
            <span className='arenaMini'><AvatarCuenta f={r} /></span>
            <span className='who'>
                <span className='arenaNombre'>
                    {r.name}{r.bot ? <span className='arenaTag'>Bot</span> : null}{r.revancha ? <span className='arenaTag'>Revancha</span> : null}
                </span>
                {r.revancha ? <span className='arenaRevancha'>Ya le retaste hoy: no gasta combate ni da premio</span> : null}
                <span className={'f-' + r.faction}>{FACCION[r.faction]}</span> · Nivel {r.level}
                <span className='arenaStats'>{STATS.map(([k, nombre]) => <span key={k}>{nombre} <b>{r.stats[k]}</b></span>)}</span>
            </span>
        </button>
    );
}

// ---------- Niveles (todo lo decide el servidor; aquí solo se elige entre lo que ofrece) ----------
const PACK = [['ataque', 'Ataque'], ['agilidad', 'Agilidad'], ['velocidad', 'Velocidad']];
const textoPack = (p) => PACK.filter(([k]) => p[k]).map(([k, nombre]) => '+' + p[k] + ' ' + nombre).join(' · ');

/** Nivel y XP; si tiene la XP al máximo sin poder subir, por qué. */
function Progreso({ p }) {
    if (!p) return null;
    const max = p.nivel >= p.nivelMax, e = p.espera;
    return (
        <div className='arenaProgreso'>
            <span className='arenaNivelTxt'>Nivel {p.nivel}{max ? ' (el máximo)' : ''}</span>
            {max ? null : (
                <span className='arenaXP' role='img' aria-label={p.experiencia + ' de ' + p.xpNivel + ' XP'}>
                    <i style={{ width: (p.experiencia / p.xpNivel) * 100 + '%' }} />
                    <b>{p.experiencia} / {p.xpNivel} XP</b>
                </span>
            )}
            {p.subio ? <p className='arenaSube'>¡Subes a nivel {p.nivel}! +10 de vida</p> : null}
            {e && e.ganar ? <p className='arenaNota'>Ya puedes subir a nivel {e.nivel}: gana un combate.</p> : null}
            {e && e.faltan ? (
                <p className='arenaNota'>
                    Tienes la XP al máximo, pero para subir a nivel {e.nivel} tiene que haber al menos {e.hay + e.faltan} personas
                    más en nivel {e.nivel - 1} o superior (ahora {e.hay === 1 ? 'hay 1' : 'hay ' + e.hay}). Cuando las haya, subirás al ganar un combate.
                </p>
            ) : null}
        </div>
    );
}

/** La mejora de una subida de nivel: 3 packs al azar que puso el servidor; se elige uno. */
export function ArenaMejora({ mejora, onStats }) {
    const [actual, setActual] = React.useState(mejora);
    const [hecho, setHecho] = React.useState(null);
    const [ocupado, setOcupado] = React.useState(false);
    const [error, setError] = React.useState('');
    const elegir = async (i) => {
        setOcupado(true); setError('');
        const r = await api('mejora', { id: actual.id, eleccion: i });
        setOcupado(false);
        if (!r.ok) return setError('No se ha podido aplicar. Recarga la página y prueba otra vez.');
        setHecho(r.pack);
        setActual(r.mejora); // si quedaba otra por elegir
        if (onStats) onStats(r.stats);
    };
    return (
        <div className='arenaMejora'>
            {hecho ? <p className='arenaGanado'>Mejora aplicada: {textoPack(hecho)}</p> : null}
            {actual ? (
                <>
                    <p>{hecho ? 'Tienes otra mejora por elegir' : 'Elige tu mejora'}</p>
                    <div className='arenaPacks'>
                        {actual.packs.map((p, i) => (
                            <button key={i} type='button' className='btnForallia arenaBtn arenaPack' disabled={ocupado} onClick={() => elegir(i)}>{textoPack(p)}</button>
                        ))}
                    </div>
                </>
            ) : null}
            {error ? <p className='arenaError'>{error}</p> : null}
        </div>
    );
}

/** Por qué da menos XP de lo normal (contra un bot o alguien de nivel inferior), o null. Las cuentas
 *  de premioDe en apps/arena/arena.js. */
function porQueMenos([yo, rival]) {
    if (rival.bot) return 'Los bots dan menos XP: 5, ganes o pierdas.';
    const sacas = yo.level - rival.level;
    if (sacas < 1) return null;
    return 'Le sacas ' + sacas + (sacas === 1 ? ' nivel' : ' niveles') + '. Contra alguien de 1 nivel menos ganas 10 XP si ganas y 5 si pierdes, '
        + 'y por cada nivel más que le saques son 2 XP menos (como mínimo 1).';
}

/** Lo que ha ganado con el combate que acaba de lanzar; si es a elegir, XP u Overbox. */
function ArenaPremio({ combate }) {
    const [premio, setPremio] = React.useState(combate.premio);
    const [progreso, setProgreso] = React.useState(combate.progreso);
    const [ocupado, setOcupado] = React.useState(false);
    const [error, setError] = React.useState('');
    const [verPorQue, setVerPorQue] = React.useState(false);
    const menos = premio.elegido === 'overbox' ? null : porQueMenos(combate.fighters);
    const elegir = async (eleccion) => {
        setOcupado(true); setError('');
        const r = await api('premio', { combateId: combate.id, eleccion });
        setOcupado(false);
        if (!r.ok) return setError('No se ha podido dar el premio. Lo tienes en el Historial para elegirlo luego.');
        setPremio(r.premio);
        setProgreso(r.progreso);
    };
    return (
        <div className='arenaPremio'>
            {premio.sinPremio ? <p className='arenaNota'>Revancha: ya le habías retado hoy, así que no da premio ni gasta combate.</p>
                : premio.elegir && !premio.elegido ? (
                    <>
                        <p>Elige tu premio</p>
                        <div className='arenaOpciones'>
                            <button type='button' className='btnForallia arenaBtn' disabled={ocupado} onClick={() => elegir('xp')}>+{premio.xp} XP</button>
                            <button type='button' className='btnForallia arenaBtn' disabled={ocupado} onClick={() => elegir('overbox')}>{premio.overbox} Overbox</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className='arenaGanado'>
                            {premio.elegido === 'overbox' ? '+' + premio.overbox + ' Overbox' : '+' + premio.xp + ' XP'}
                            {menos ? <button type='button' className='arenaPorQue' aria-label='¿Por qué tan poca XP?' aria-expanded={verPorQue}
                                onClick={() => setVerPorQue(!verPorQue)}><img src={infoImage} alt='' /></button> : null}
                        </p>
                        {menos && verPorQue ? <p className='arenaNota'>{menos}</p> : null}
                    </>
                )}
            {error ? <p className='arenaError'>{error}</p> : null}
            <Progreso p={progreso} />
            {progreso && progreso.mejora ? <ArenaMejora key={progreso.mejora.id} mejora={progreso.mejora} /> : null}
        </div>
    );
}

// ---------- Habilidades, armas y mascotas (de momento no cambian nada en el combate) ----------
const EFECTOS = [['vida', 'Vida'], ['ataque', 'Ataque'], ['agilidad', 'Agilidad'], ['velocidad', 'Velocidad']];
export const textoEfectos = (o) => EFECTOS.filter(([k]) => o.efectos[k]).map(([k, nombre]) => nombre + ' ' + o.efectos[k])
    .concat(o.tipo === 'arma' ? [o.usosMin + (o.usosMax > o.usosMin ? '–' + o.usosMax : '') + ' usos'] : []).join(' · ');

export function ObjetoArena({ o }) {
    return (
        <span className='arenaObjeto' title={o.descripcion || undefined}>
            {o.imagen ? <img className='arenaImg' src={o.imagen} alt='' /> : null}
            <span><b>{o.nombre}</b> <span className='arenaNota'>{textoEfectos(o)}</span></span>
        </span>
    );
}

/** Subir de tier: el luchador vuelve a nivel 1 a cambio de una de las 3 que ofrece el servidor. */
function ArenaTier({ oferta, tier, onHecho }) {
    const [ocupado, setOcupado] = React.useState(false);
    const [error, setError] = React.useState('');
    const elegir = async (o) => {
        if (!window.confirm('Tu luchador vuelve a nivel 1 con las stats con las que empezó (pierdes la XP y los puntos de las subidas de nivel), pasas a tier ' + (tier + 1) + ' y te llevas ' + o.nombre + '. ¿Seguro?')) return;
        setOcupado(true); setError('');
        const r = await api('tier', { id: oferta.id, objetoId: o.id });
        setOcupado(false);
        if (!r.ok) return setError('No se ha podido. Recarga la página y prueba otra vez.');
        onHecho(r);
    };
    return (
        <div className='arenaMejora'>
            <p>Subir a tier {tier + 1}</p>
            <p className='arenaNota'>Tu luchador vuelve a nivel 1 con las stats con las que empezó, a cambio de una de estas:</p>
            <div className='arenaPacks'>
                {oferta.opciones.map(o => (
                    <button key={o.id} type='button' className='btnForallia arenaBtn arenaPack' disabled={ocupado} onClick={() => elegir(o)}><ObjetoArena o={o} /></button>
                ))}
            </div>
            {error ? <p className='arenaError'>{error}</p> : null}
        </div>
    );
}

/** En la pestaña Stats de tu cuenta: tu nivel, tu XP, tus habilidades, la mejora que te quede por
 *  elegir y, si puedes, subir de tier. onEstado recibe el estado (la pestaña Equipo sale de ahí). */
export function ArenaNivel({ onStats, onEstado }) {
    const [e, setE] = React.useState(null);
    const cargar = React.useCallback(() => api('estado').then(r => {
        if (!r.ok) return;
        setE(r);
        if (onEstado) onEstado(r);
    }), [onEstado]);
    React.useEffect(() => { cargar(); }, [cargar]);
    if (!e) return null;
    const habilidades = e.objetos.filter(o => o.tipo === 'habilidad');
    return (
        <div className='arenaNivel'>
            {/* Todos empiezan en tier 1: de momento solo se enseña a partir del 2 */}
            {e.tier > 1 ? <span className='arenaNivelTxt'>Tier {e.tier}</span> : null}
            <Progreso p={e.progreso} />
            {habilidades.length ? (
                <div className='arenaObjetos'>
                    <span className='arenaNivelTxt'>Habilidades</span>
                    {habilidades.map(o => <ObjetoArena key={o.id} o={o} />)}
                </div>
            ) : null}
            {e.mejora ? <ArenaMejora key={e.mejora.id} mejora={e.mejora} onStats={onStats} /> : null}
            {e.tierOferta ? <ArenaTier key={e.tierOferta.id} oferta={e.tierOferta} tier={e.tier} onHecho={(r) => { if (onStats) onStats(r.stats); cargar(); }} /> : null}
        </div>
    );
}

const EQUIPO = [['armas', 'arma', 'Armas', 3], ['mascotas', 'mascota', 'Mascotas', 2]];

/** Pestaña Equipo de tu cuenta: hasta 3 armas y 2 mascotas de las tuyas. Se guarda al tocarlas. */
export function ArenaEquipo({ objetos, equipo: inicial }) {
    const [equipo, setEquipo] = React.useState(inicial);
    const [error, setError] = React.useState('');
    const cambiar = async (k, id) => {
        const nuevo = { ...equipo, [k]: equipo[k].includes(id) ? equipo[k].filter(x => x !== id) : equipo[k].concat(id) };
        setEquipo(nuevo); setError('');
        const r = await api('equipo', nuevo);
        if (!r.ok) { setEquipo(equipo); setError('No se ha podido guardar. Prueba otra vez.'); }
    };
    return (
        <div className='infoTwitch flexColumn arenaNivel arenaEquipo'>
            {EQUIPO.map(([k, tipo, titulo, max]) => {
                const mias = objetos.filter(o => o.tipo === tipo);
                return mias.length ? (
                    <div key={k} className='arenaObjetos'>
                        <span className='arenaNivelTxt'>{titulo} ({equipo[k].length}/{max})</span>
                        {mias.map(o => {
                            const puesto = equipo[k].includes(o.id);
                            return (
                                <button key={o.id} type='button' aria-pressed={puesto} className={'arenaEquipar' + (puesto ? ' puesto' : '')}
                                    disabled={!puesto && equipo[k].length >= max} onClick={() => cambiar(k, o.id)}>
                                    <ObjetoArena o={o} />
                                </button>
                            );
                        })}
                    </div>
                ) : null;
            })}
            {error ? <p className='arenaError'>{error}</p> : null}
        </div>
    );
}

/** Popup de la arena: elige contrincante y lucha. Con combateId, vuelve a ver ese combate del historial. */
export function ArenaPopUp({ combateId, onClose }) {
    // El combate del historial que se está viendo; con "Luchar de nuevo" se pasa a elegir contrincante.
    const [verId, setVerId] = React.useState(combateId);
    const [disponibles, setDisponibles] = React.useState(null);
    const [progreso, setProgreso] = React.useState(null);
    const [rivales, setRivales] = React.useState(null);
    const [busqueda, setBusqueda] = React.useState('');
    const [encontrados, setEncontrados] = React.useState(null);
    const [combate, setCombate] = React.useState(null);
    const [error, setError] = React.useState('');
    const [ocupado, setOcupado] = React.useState(false);
    // Cuándo se libera el siguiente combate, en la hora de este navegador.
    const [libera, setLibera] = React.useState(0);
    const [ahora, setAhora] = React.useState(Date.now());
    // Los combates cuentan para el paso de la arena del tutorial (los cuenta el servidor).
    const tutorial = useTutorial();

    const leer = React.useCallback((r) => {
        setDisponibles(r.disponibles);
        setLibera(r.siguienteEn ? Date.now() + r.siguienteEn : 0);
        setAhora(Date.now());
    }, []);
    const cargar = React.useCallback(async () => {
        setError('');
        const e = await api('estado');
        if (!e.ok) return setError(mensaje(e));
        leer(e);
        setProgreso(e.progreso);
        if (!e.disponibles) return setRivales(null);
        const r = await api('rivales');
        if (r.ok) setRivales(r.rivales);
        else setError(mensaje(r));
    }, [leer]);

    React.useEffect(() => {
        if (!combateId) { cargar(); return; }
        api('combate/' + combateId).then(r => (r.ok ? setCombate(r) : setError(mensaje(r))));
    }, [combateId, cargar]);

    // Cuenta atrás. Al llegar a cero se vuelve a pedir el estado: si no quedaba ninguno, también
    // los contrincantes; si quedaban, solo los puntos, para no cambiarle los que está mirando.
    React.useEffect(() => {
        if (!libera || combate) return undefined;
        const t = setInterval(() => setAhora(Date.now()), 1000);
        return () => clearInterval(t);
    }, [libera, combate]);
    React.useEffect(() => {
        if (!libera || combate || ahora < libera) return;
        setLibera(0);
        if (disponibles) api('estado').then(e => e.ok && leer(e));
        else cargar();
    }, [ahora, libera, combate, disponibles, leer, cargar]);

    // Buscador: espera a que deje de escribir y descarta las respuestas que llegan tarde.
    React.useEffect(() => {
        const q = busqueda.trim();
        setEncontrados(null);
        if (q.length < 2) return undefined;
        let vivo = true;
        const t = setTimeout(() => api('buscar?q=' + encodeURIComponent(q)).then(r => {
            if (!vivo) return;
            if (r.ok) setEncontrados(r.rivales);
            else setError(mensaje(r));
        }), 300);
        return () => { vivo = false; clearTimeout(t); };
    }, [busqueda]);

    // Con un combate recién lanzado solo se sale por sus botones del final: ni Escape, ni clicar fuera,
    // ni X. Viendo uno del historial (verId) sí se cierra con Escape o clicando fuera.
    const cerrable = !combate || !!verId;
    React.useEffect(() => {
        if (!cerrable) return undefined;
        const escape = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', escape);
        return () => document.removeEventListener('keydown', escape);
    }, [onClose, cerrable]);

    const luchar = async (rival) => {
        setError(''); setOcupado(true);
        const r = await api('luchar', { rivalId: rival.id });
        // También si falla: con un 409 el tutorial se entera de que ha empezado el directo.
        if (tutorial) tutorial.avisar('combate');
        if (r.ok) { setOcupado(false); return setCombate(r); }
        // Falle por lo que falle, combates y contrincantes se vuelven a pedir: el servidor puede haber
        // gastado el combate aunque la respuesta no llegara, o haber caducado la oferta (10 minutos).
        setBusqueda('');
        await cargar();
        setOcupado(false);
        setError(mensaje(r));
    };
    const lucharDeNuevo = () => {
        setVerId(null); setCombate(null); setRivales(null); setDisponibles(null); setBusqueda('');
        cargar();
    };
    const buscando = busqueda.trim().length >= 2;
    const lista = buscando ? encontrados : rivales;

    let contenido;
    if (combate) {
        // El premio solo en los combates que acaba de lanzar (al verlos desde el historial no hay).
        contenido = (
            <ArenaCombate combate={combate} onLucharDeNuevo={lucharDeNuevo} onCerrar={onClose}>
                {combate.premio ? <ArenaPremio combate={combate} /> : null}
            </ArenaCombate>
        );
    } else if (verId) {
        contenido = <p className='arenaNota'>{error || 'Cargando…'}</p>;
    } else {
        contenido = (
            <>
                <div className='PopUpTitle'><h3>Elige contrincante</h3></div>
                <div className='arenaPopUpBody'>
                    {disponibles === null ? <p className='arenaNota'>{error ? '' : 'Cargando…'}</p> : (
                        <p className='arenaNota'>
                            Combates <span className='arenaUsos' role='img' aria-label={disponibles + (disponibles === 1 ? ' combate' : ' combates')}>{corazones(disponibles)}</span>
                            {libera ? ' · el siguiente se recupera en ' + mmss(libera - ahora) : ''}
                        </p>
                    )}
                    {progreso ? <p className='arenaNota'>Nivel {progreso.nivel}{progreso.nivel < progreso.nivelMax ? ' · ' + progreso.experiencia + ' / ' + progreso.xpNivel + ' XP' : ''}</p> : null}
                    {disponibles === 0 ? <p className='arenaNota'>Sin combates puedes hacer revancha: busca a alguien a quien ya hayas retado hoy (no da premio).</p> : null}
                    {disponibles !== null ? (
                        <input className='userSearch arenaBuscar' type='search' placeholder='Busca a quien quieras retar' aria-label='Buscar contrincante'
                            maxLength={64} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                    ) : null}
                    {lista && lista.length ? (
                        <div className='arenaRivales'>
                            {lista.map(r => <Rival key={r.id} r={r} disabled={ocupado || (disponibles === 0 && !r.revancha)} onClick={() => luchar(r)} />)}
                        </div>
                    ) : null}
                    {buscando && !encontrados ? <p className='arenaNota'>Buscando…</p> : null}
                    {lista && !lista.length ? <p className='arenaNota'>{buscando ? 'No hay nadie con ese nombre.' : 'Ahora mismo no hay contrincantes. Prueba en un rato.'}</p> : null}
                    {error ? <p className='arenaError'>{error}</p> : null}
                </div>
            </>
        );
    }

    // En el body: dentro de Mi cuenta heredaría estilos de donde se abre (.infoTwitch img mide 25px de alto).
    return ReactDOM.createPortal(
        <div className='overboxGetMoreOverlay' onClick={cerrable ? onClose : undefined}>
            <div className='PopUp heroes arenaPopUp' role='dialog' aria-modal='true' aria-label={combate ? 'Combate' : 'Elige contrincante'} onClick={(e) => e.stopPropagation()}>
                {!combate ? <span className='PopUpClose' onClick={onClose}>X</span> : null}
                {contenido}
            </div>
        </div>,
        document.body
    );
}

/** Pestaña Historial de Mi cuenta: los últimos combates, lanzados y recibidos, y verlos otra vez. */
export function ArenaHistorial() {
    const [combates, setCombates] = React.useState(null);
    const [error, setError] = React.useState('');
    const [viendo, setViendo] = React.useState(null);
    const cargar = React.useCallback(() => {
        api('estado').then(r => (r.ok ? setCombates(r.combates) : setError(mensaje(r))));
    }, []);
    React.useEffect(() => { cargar(); }, [cargar]);
    // El premio que se quedó sin elegir al cerrar el combate
    const elegirPremio = async (c, eleccion) => {
        const r = await api('premio', { combateId: c.id, eleccion });
        if (!r.ok) setError('No se ha podido dar el premio. Prueba otra vez.');
        cargar();
    };

    return (
        <div className='infoTwitch flexColumn arenaHist'>
            {error ? <p className='arenaError'>{error}</p>
                : !combates ? <p className='arenaNota'>Cargando…</p>
                    : !combates.length ? <p className='arenaNota'>Aún no has luchado.</p>
                        : combates.map(c => (
                            <div key={c.id} className='arenaHistFila'>
                                <span className='arenaHistTexto'>
                                    <span className={'fH ' + (c.victoria ? 'gana' : 'pierde')}>{c.victoria ? 'Victoria' : 'Derrota'}</span>
                                    <span>{c.lance ? 'Retaste a ' : 'Te retó '}<span className={'f-' + c.rival.faction}>{c.rival.name}</span>{c.rival.bot ? ' (bot)' : ''} · {c.motivo === 'ko' ? 'KO' : 'a los puntos'}</span>
                                    <span className='d'>{fecha(c.fecha)}</span>
                                    {c.premioPendiente ? (
                                        <span className='arenaOpciones'>
                                            Premio sin elegir:
                                            <button type='button' className='btnForallia arenaBtn' onClick={() => elegirPremio(c, 'xp')}>+{c.premioPendiente.xp} XP</button>
                                            <button type='button' className='btnForallia arenaBtn' onClick={() => elegirPremio(c, 'overbox')}>{c.premioPendiente.overbox} Overbox</button>
                                        </span>
                                    ) : null}
                                </span>
                                <button type='button' className='btnForallia arenaBtn' onClick={() => setViendo(c.id)}>Ver</button>
                            </div>
                        ))}
            {/* Al cerrar se vuelve a pedir: desde el popup se puede haber luchado ("Luchar de nuevo") */}
            {viendo ? <ArenaPopUp combateId={viendo} onClose={() => { setViendo(null); cargar(); }} /> : null}
        </div>
    );
}
